// The hero's call to action (Home.tsx): a prompt, already typed in, in a box
// drawn like an AI chat's own composer. Where a composer has its send button,
// this one has a tray of each AI's mark, each opening a new chat with
// the prompt typed in through its ?q= link. Opening an AI copies the prompt
// too, for a chat that opens empty; for any other AI, it's short enough to
// type. The links are real hrefs from the first render, so they work before
// the page hydrates.

import { useEffect, useRef, useState } from 'react';
import { AI_LINKS } from '../content/quickstart';
import { AiMark, type AiId } from './AiMark';

const AIS: { id: AiId; name: string; href: (p: string) => string }[] = [
  { id: 'chatgpt', name: 'ChatGPT', href: AI_LINKS.chatgpt },
  { id: 'claude', name: 'Claude', href: AI_LINKS.claude },
  { id: 'grok', name: 'Grok', href: AI_LINKS.grok },
];

/** `short`, if given, is what the box shows on a phone instead of `prompt`;
 *  its links open `prompt` either way. */
export function PromptBox({ prompt, short }: { prompt: string; short?: string }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(timer.current), []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
    } catch {
      return;   // Clipboard access can be blocked; the chat still opens with the prompt.
    }
    setCopied(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="prompt-box" role="group" aria-label="A prompt for your AI">
      {/* One line, however narrow the screen: on a phone the short
          version, and whatever still doesn't fit ends in an ellipsis, the
          caret still after it. Both are in the page and CSS picks one, so
          the prerendered HTML is already right. The links always open the
          whole prompt. */}
      <p className="prompt-box-text">
        <span className={`prompt-box-line${short ? ' prompt-box-full' : ''}`} title={prompt}>
          {prompt}
        </span>
        {short ? (
          <span className="prompt-box-line prompt-box-short" title={prompt}>
            {short}
          </span>
        ) : null}
      </p>
      <div className="prompt-box-actions">
        {AIS.map((ai) => (
          <a
            key={ai.id}
            className={`prompt-box-btn prompt-box-${ai.id}`}
            href={ai.href(prompt)}
            target="_blank"
            rel="noopener"
            aria-label={`Open in ${ai.name}`}
            data-tip={ai.name}
            onClick={copy}
          >
            <AiMark id={ai.id} />
          </a>
        ))}
        <span className="sr-only" aria-live="polite">
          {copied ? 'Prompt copied' : ''}
        </span>
      </div>
    </div>
  );
}

